<template>
  <div :class="['ai-chat-attachment-tray', large ? '-large' : '', smAndDown ? '-mobile' : '']">
    <component
      :is="wrapper(attachment)"
      v-for="attachment in attachments"
      :key="attachment.key"
      :class="['_item', attachment.isImage ? '-image' : '-file']"
      v-bind="bindings(attachment)"
      @click="zoom(attachment)"
    >
      <template v-if="attachment.isImage">
        <img
          v-if="attachment.url !== null"
          class="_thumb"
          :src="attachment.url"
          :alt="attachment.name"
        >
        <div
          v-else
          class="_thumb -loading"
        >
          <m-spinner size="sm" />
        </div>
      </template>
      <template v-else>
        <div class="_icon">
          <m-icon
            :type="attachmentIcon(attachment.name)"
            size="20"
            :color="$colors.grey.darken1"
          />
        </div>
        <div class="_meta">
          <div class="_name">
            {{ attachment.name }}
          </div>
          <div class="_kind">
            {{ kindLabel(attachment.name) }}
          </div>
        </div>
        <m-spinner
          v-if="attachment.uploading"
          size="xs"
        />
      </template>
      <m-btn
        v-if="removable"
        class="_remove"
        icon="close"
        icon-size="12"
        :icon-color="$colors.shades.white"
        force-icon-color
        fab
        xs
        :disabled="attachment.uploading"
        @click="remove($event, attachment.key)"
      />
    </component>
    <m-image-lightbox
      v-if="zoomed !== null"
      :src="zoomed.url"
      :alt="zoomed.name"
      :file-name="zoomed.name"
      @close="zoomed = null"
    />
  </div>
</template>

<script setup>
import { attachmentIcon, attachmentKind, extensionOf } from '@/lib/attachment';
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';

const props = defineProps({
  attachments: {
    type: Array,
    required: true,
  },
  removable: {
    type: Boolean,
    default: false,
  },
  large: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['remove']);

const { t } = useI18n();
const store = useStore();

const zoomed = ref(null);

const smAndDown = computed(() => store.state.breakpoint.smAndDown);

const kindLabel = (name) => t(
  `fileType.${attachmentKind(name)}`,
  { extension: extensionOf(name).toUpperCase() },
);

// a picture opens in place, so it stays next to the conversation it belongs to; anything
// else is a file to take away, which only the browser can do
const isDownload = (attachment) => !attachment.isImage && !props.removable && attachment.url !== null;

const wrapper = (attachment) => (isDownload(attachment) ? 'a' : 'div');

const bindings = (attachment) => (isDownload(attachment)
  ? { href: attachment.url, target: '_blank', rel: 'noopener' }
  : {});

// the item underneath opens the picture, which is not what this button is for
const remove = (event, key) => {
  event.stopPropagation();
  emit('remove', key);
};

const zoom = (attachment) => {
  if (attachment.isImage && attachment.url !== null) {
    zoomed.value = attachment;
  }
};
</script>

<style
  scoped
  lang="scss"
  type="text/scss"
>
  // every attachment is this tall, whatever shape it is, so the row reads as one strip
  $attachment-height: 6.4rem;

  .ai-chat-attachment-tray {
    display: flex;
    flex-wrap: nowrap;
    gap: .8rem;
    overflow-x: auto;

    // a visible bar under the attachments reads as a second row of chrome in a composer
    // this small; setting overflow-x alone would also turn overflow-y into a scrollbar
    overflow-y: hidden;
    scrollbar-width: none;

    ._item {
      position: relative;
      flex-shrink: 0;
      height: $attachment-height;
      color: inherit;
      text-decoration: none;
    }

    ._remove {
      position: absolute;
      top: .4rem;
      right: .4rem;
      background-color: $font-color-secondary;
      opacity: 0;
      transition: opacity .1s ease-in-out, background-color .1s ease-in-out;

      &:hover,
      &:focus-visible {
        background-color: $font-color-primary;
      }

      &:focus-visible {
        opacity: 1;
      }
    }

    // nothing hovers on a touch screen, and the composer is the whole screen there
    &.-mobile ._remove {
      opacity: 1;
    }

    ._item:hover ._remove {
      opacity: 1;
    }

    ._item.-image {
      width: $attachment-height;
      cursor: pointer;

      ._thumb {
        width: 100%;
        height: 100%;
        object-fit: cover;
        background-color: white;
        border: 1px solid $border-color;
        border-radius: 1.2rem;
      }

      ._thumb.-loading {
        display: flex;
        align-items: center;
        justify-content: center;
      }
    }

    ._item.-file {
      display: flex;
      align-items: center;
      max-width: 26rem;
      gap: 1rem;
      padding: .8rem 1.2rem;
      background-color: white;
      border: 1px solid $border-color;
      border-radius: 1.2rem;

      ._icon {
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
        width: 3.2rem;
        height: 3.2rem;
        background-color: $hover-color;
        border-radius: .8rem;
      }

      ._meta {
        min-width: 0;
      }

      ._name {
        overflow: hidden;
        font-size: $font-size-2;
        font-weight: $font-weight-semibold;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      ._kind {
        font-size: $font-size-1;
        color: $font-color-secondary;
      }
    }

    // a sent attachment stacks rather than filling rows, and takes the side its message
    // sits on, which only the message knows
    &.-large {
      flex-direction: column;
      align-items: inherit;
      max-width: 100%;
      overflow-x: visible;
    }

    &.-large ._item {
      height: auto;
    }

    // width, not align-self: stretch, which lays the chip out from the opposite side and
    // then clips it, leaving the cap's difference as a gap on the edge it should sit against
    &.-large ._item.-file {
      width: 100%;
      min-width: 0;
      max-width: 22rem;
    }

    &.-large ._item.-image {
      width: auto;

      ._thumb {
        display: block;
        width: auto;
        max-width: 24rem;
        height: auto;
        max-height: 20rem;
        object-fit: contain;
        cursor: pointer;
      }

      ._thumb.-loading {
        width: $attachment-height;
        height: $attachment-height;
      }
    }
  }
</style>
